import type { JSX } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, FileCheck, Clock, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useImpersonation } from '@/features/auth/hooks/useImpersonation';
import { useCompanyUsers } from '../hooks/useCompanyUsers';

interface QuickActionCardProps {
  title: string;
  description: string;
  icon: React.ElementType;
  onClick: () => void;
}

function QuickActionCard({
  title,
  description,
  icon: Icon,
  onClick,
}: QuickActionCardProps): React.JSX.Element {
  return (
    <Card className="cursor-pointer transition-colors hover:bg-muted/50" onClick={onClick}>
      <CardHeader className="flex flex-row items-center gap-4 space-y-0 pb-2">
        <div className="rounded-lg bg-primary/10 p-2">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <div>
          <CardTitle className="text-base">{title}</CardTitle>
          <CardDescription className="text-sm">{description}</CardDescription>
        </div>
      </CardHeader>
    </Card>
  );
}

interface StatsCardProps {
  title: string;
  value: number | string;
  description?: string | undefined;
  icon: React.ElementType;
}

function StatsCard({ title, value, description, icon: Icon }: StatsCardProps): React.JSX.Element {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </CardContent>
    </Card>
  );
}

export function CompanyAdminDashboardPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { impersonatedContext, isImpersonating } = useImpersonation();
  const { users } = useCompanyUsers(id);

  const companyName = impersonatedContext?.companyName ?? 'Company';
  const userCount = users.length;

  const quickActions = [
    {
      title: 'Manage Users',
      description: 'View and manage company users',
      icon: Users,
      onClick: () => navigate(`/companies/${id}/users`),
    },
    {
      title: 'Verifications',
      description: 'View verification requests',
      icon: FileCheck,
      onClick: () => {},
    },
  ];

  if (!isImpersonating) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <p className="text-muted-foreground">Please enter a company to view the dashboard.</p>
      </div>
    );
  }

  return (
    <div className="container py-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">{companyName} Overview</p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatsCard title="Total Users" value={userCount} icon={Users} />
        <StatsCard
          title="Active Verifications"
          value={0}
          description="Coming soon"
          icon={FileCheck}
        />
        <StatsCard title="Pending Requests" value={0} description="Coming soon" icon={Clock} />
        <StatsCard title="This Month" value={0} description="Coming soon" icon={TrendingUp} />
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="mb-4 text-lg font-semibold">Quick Actions</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {quickActions.map((action) => (
            <QuickActionCard key={action.title} {...action} />
          ))}
        </div>
      </div>
    </div>
  );
}
